import { useState } from "react";
import { createFileRoute, Link, redirect, useNavigate, useRouter } from "@tanstack/react-router";

import { getCurrentUserFn, loginFn } from "@/server/auth.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BrainingLogo } from "@/braining/components/braining-logo";

/**
 * Вход. Собран по образцу `src/routes/login.tsx` из ядра, но в оформлении
 * Braining и с возвратом на список калькуляторов, а не на дашборд Alpme.
 */
export const Route = createFileRoute("/login")({
  head: () => ({ meta: [{ title: "Вход — Портал Braining Group" }] }),
  beforeLoad: async () => {
    const user = await getCurrentUserFn();
    if (user) throw redirect({ to: "/" });
  },
  component: LoginPage,
});

function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const router = useRouter();

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      const result = await loginFn({ data: { email, password } });
      if (result.ok) {
        await router.invalidate();
        await navigate({ to: "/" });
      } else {
        setError(result.error);
      }
    } catch (err) {
      console.error("[login] failed", err);
      setError("Не удалось войти. Попробуйте ещё раз.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex justify-center">
          <BrainingLogo className="h-11 w-auto text-foreground" />
        </div>

        <form onSubmit={onSubmit} className="space-y-4 rounded-xl border bg-card p-6 shadow-sm">
          <h1 className="text-lg font-semibold">Вход</h1>

          <div className="space-y-1.5">
            <Label htmlFor="email">Почта</Label>
            <Input
              id="email"
              type="email"
              autoComplete="username"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={submitting}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="password">Пароль</Label>
            <Input
              id="password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={submitting}
            />
          </div>

          {error && (
            <p className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive-ink">
              {error}
            </p>
          )}

          <Button type="submit" className="w-full" disabled={submitting}>
            {submitting ? "Входим…" : "Войти"}
          </Button>
        </form>

        {/* Ссылка снаружи входа: Google хочет, чтобы политика открывалась с
            главной страницы приложения, а главная у нас — экран входа. */}
        <p className="mt-6 text-center text-xs text-muted-foreground">
          <Link to="/privacy" className="hover:underline">
            Данные и доступы
          </Link>
        </p>
      </div>
    </div>
  );
}
